import {
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	Grid2,
	IconButton,
	MenuItem,
	Select,
	Typography,
} from '@mui/material';
import { CloseIcon, UploadIcon } from '../../assets/icons/Icons';
import { helperText } from '../../assets/theme/Theme';
import Dropzone from 'react-dropzone';
import { MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import TextInput from '../../components/TextInput';

export interface ImageLabel {
	crop: string;
	condition: string;
	type: string;
	conditionDisplay: string;
	searchTerm: string;
}

interface PopupProps {
	isModalOpen: boolean;
	selectedImages: File[];
	label: ImageLabel;
	onChangeLabel: (field: keyof ImageLabel, value: string) => void;
	onClose: () => void;
	onSubmit: () => void;
	onSelectImages: (files: File[]) => void;
	isUploading: boolean;
	error: { status: boolean; msg: string };
	cropOptions: string[];
	typeOptions: string[];
	isOptionsLoading: boolean;
}

function UploadImagePopup({
	isModalOpen,
	selectedImages,
	label,
	onChangeLabel,
	onClose,
	onSubmit,
	onSelectImages,
	isUploading,
	error,
	cropOptions,
	typeOptions,
	isOptionsLoading,
}: PopupProps) {
	function isFieldEmpty(field: keyof ImageLabel) {
		return error.status && label[field].trim() === '';
	}

	function renderSelect(
		field: keyof ImageLabel,
		title: string,
		options: string[],
	) {
		return (
			<Grid2 container width={'100%'} marginTop={'10px'}>
				<Typography
					fontSize={'16px'}
					width={'100%'}
					color={'#7C7C7C'}
					paddingBottom={'10px'}>
					{title}
				</Typography>
				<Select
					size="small"
					fullWidth
					displayEmpty
					value={label[field]}
					error={isFieldEmpty(field)}
					disabled={isOptionsLoading}
					renderValue={value => {
						if (!value) {
							return (
								<Typography color="#cacdd4">
									{isOptionsLoading
										? 'Loading...'
										: `Select ${title}`}
								</Typography>
							);
						}
						return value;
					}}
					onChange={e => onChangeLabel(field, e.target.value)}
					sx={{
						backgroundColor: '#FFFFFF',
						color: '#777E90',
					}}>
					{options.map(item => (
						<MenuItem key={item} value={item}>
							{item}
						</MenuItem>
					))}
				</Select>
			</Grid2>
		);
	}

	return (
		<>
			<Dialog open={isModalOpen} fullWidth maxWidth="md" scroll="body">
				<DialogContent style={{ padding: '0px 0px 20px 0px' }}>
					<Grid2
						container
						sx={{ boxShadow: 3 }}
						padding={'10px'}
						flexDirection={'row'}
						justifyContent={'space-between'}
						alignItems={'center'}>
						<Typography fontWeight={'600'}>
							Upload Images
						</Typography>
						<IconButton style={{ padding: '0' }} onClick={onClose}>
							<CloseIcon />
						</IconButton>
					</Grid2>
					<Grid2 container padding={'15px'}>
						<Grid2
							container
							width={'100%'}
							flexDirection={'column'}>
							<Dropzone
								accept={{ 'image/*': [] }}
								multiple
								onDrop={acceptedFiles =>
									onSelectImages(acceptedFiles)
								}>
								{({ getRootProps, getInputProps }) => (
									<section style={{ width: '100%' }}>
										<div
											{...getRootProps()}
											style={{
												backgroundColor: '#FFFFFF',
												borderRadius: '5px',
												padding: '50px',
												textAlign: 'center',
												marginBottom: '10px',
												border: '1px dashed #D9D9D9',
											}}>
											<input {...getInputProps()} />
											<UploadIcon />
											<br />
											<Typography
												fontSize={'16px'}
												fontWeight={'700'}
												color={'#7C7C7C'}>
												Select images here...
											</Typography>
										</div>
										<Grid2
											container
											alignItems={'center'}
											flexDirection={'row'}
											gap={'5px'}>
											{selectedImages.map(image => (
												<Grid2
													key={image.name}
													bgcolor={'#EEF1F7'}
													borderRadius={'5px'}
													padding={
														'5px 10px 5px 10px'
													}
													sx={{
														boxShadow: 2,
														marginBottom: '5px',
														overflow: 'hidden',
													}}>
													<Typography
														style={{
															wordBreak:
																'break-all',
														}}
														color={'#292D32'}
														fontSize={'14px'}>
														{image.name}
													</Typography>
												</Grid2>
											))}
										</Grid2>
									</section>
								)}
							</Dropzone>
							{renderSelect('crop', 'Crop', cropOptions)}
							{renderSelect('type', 'Type', typeOptions)}
							<Grid2 marginTop={'10px'}>
								<TextInput
									inputTitle={'Condition (e.g. leaf_curl)'}
									value={label.condition}
									onChange={value =>
										onChangeLabel('condition', value)
									}
									inputError={{
										status: isFieldEmpty('condition'),
										msg: '',
									}}
								/>
							</Grid2>
							<Grid2 marginTop={'10px'}>
								<TextInput
									inputTitle={
										'Condition display name (e.g. leaf curl)'
									}
									value={label.conditionDisplay}
									onChange={value =>
										onChangeLabel('conditionDisplay', value)
									}
									inputError={{ status: false, msg: '' }}
								/>
							</Grid2>
							<Grid2 marginTop={'10px'}>
								<TextInput
									inputTitle={
										'Search term (e.g. chilli leaf curl virus)'
									}
									value={label.searchTerm}
									onChange={value =>
										onChangeLabel('searchTerm', value)
									}
									inputError={{ status: false, msg: '' }}
								/>
							</Grid2>
							<Typography style={helperText}>
								{error.msg}
							</Typography>
						</Grid2>
						<Grid2
							container
							justifyContent={'flex-end'}
							paddingTop={'10px'}
							width={'100%'}
							flexDirection={'row'}>
							<Button
								onClick={onClose}
								sx={{
									paddingLeft: '15px',
									paddingRight: '15px',
									color: '#7F7C7C',
									border: '1px solid #7F7C7C',
									fontWeight: '600',
									textTransform: 'none',
									marginRight: '10px',
								}}>
								{'Cancel'}
							</Button>
							<IconButton
								disableRipple
								disabled={isUploading}
								onClick={onSubmit}
								sx={{
									backgroundColor: MAIN_THEME_COLOR,
									paddingX: '10px',
									color: '#FFFFFF',
									textTransform: 'none',
									borderRadius: '5px',
									height: '40px',
									alignItems: 'center',
									width: '80px',
									marginRight: '5px',
									'&.Mui-disabled': {
										backgroundColor: MAIN_THEME_COLOR,
									},
								}}>
								{isUploading ? (
									<CircularProgress
										sx={{ color: '#FFFFFF' }}
										size={16}
									/>
								) : (
									<Typography
										fontWeight={'600'}
										fontSize={'14px'}
										color={'#FFFFFF'}>
										Upload
									</Typography>
								)}
							</IconButton>
						</Grid2>
					</Grid2>
				</DialogContent>
			</Dialog>
		</>
	);
}

export default UploadImagePopup;
